// Builder demo: a reference integration that only uses the public API.
import type { Layer } from '../shared/engrave.ts';
import type { Option, PublicStone, RenderResponse } from '../shared/api.ts';
import { $, api, esc } from './dom.ts';

const API = ''; // e.g. 'https://stones.example.com' when embedded on another site
const SLIDERS = ['size', 'tilt', 'turn', 'offsetY', 'decorScale', 'decorOffsetY'] as const; // what customers may tweak here
const SELECTS = ['material', 'font', 'color', 'decor'] as const;

let stone: PublicStone;
let abort: AbortController | undefined;
let timer: ReturnType<typeof setTimeout> | undefined;

const layerImg: Record<Layer, HTMLImageElement> = { text: $('textLayer'), decor: $('decorLayer') };

const fillSelect = (sel: HTMLSelectElement, list: Option<string>[], value: string) =>
  (sel.innerHTML = list.map((o) => `<option value="${o.key}" ${o.key === value ? 'selected' : ''}>${esc(o.label)}</option>`).join(''));

async function loadStone(slug: string): Promise<void> {
  stone = await api<PublicStone>(`${API}/api/stones/${slug}`);
  $<HTMLImageElement>('stoneImg').src = stone.imageUrl;
  $<HTMLTextAreaElement>('text').value = stone.defaults.text;
  fillSelect($('material'), stone.materials.map((m) => ({ key: m.slug, label: m.name })), stone.materials[0]?.slug ?? '');
  fillSelect($('font'), stone.options.fonts, stone.defaults.font);
  // "Recommended" = let the API use the material's recommended color (e.g. gold on dark granite)
  fillSelect($('color'), [{ key: '', label: 'Recommended' }, ...stone.options.colors], '');
  fillSelect($('decor'), stone.options.decor, stone.defaults.decor);
  $('sliders').innerHTML = SLIDERS.map((k) => {
    const p = stone.params[k];
    return `<label><span class="row"><span>${esc(p.label)}</span><span id="${k}V">${stone.defaults[k]}</span></span></label>
            <input type="range" id="${k}" min="${p.min}" max="${p.max}" step="${p.step}" value="${stone.defaults[k]}">`;
  }).join('');
  for (const k of SLIDERS) {
    $<HTMLInputElement>(k).addEventListener('input', (e) => {
      $(`${k}V`).textContent = (e.target as HTMLInputElement).value;
      update();
    });
  }
  update();
}

/** Swap a layer image only once the new PNG is decoded (no flicker while typing). */
async function showLayer(layer: Layer, r: RenderResponse): Promise<void> {
  const el = layerImg[layer];
  const l = r.layers[layer];
  if (!l) {
    el.hidden = true;
    return;
  }
  if (el.dataset.url !== l.url) {
    const img = new Image();
    img.src = l.url;
    await img.decode();
    el.src = l.url;
    el.dataset.url = l.url;
  }
  Object.assign(el.style, { left: `${l.percent.left}%`, top: `${l.percent.top}%`, width: `${l.percent.width}%`, height: `${l.percent.height}%` });
  el.hidden = false;
}

function update(): void {
  clearTimeout(timer);
  timer = setTimeout(async () => {
    abort?.abort(); // cancel stale requests while typing
    abort = new AbortController();
    const qs = new URLSearchParams({ text: $<HTMLTextAreaElement>('text').value });
    for (const k of SELECTS) {
      const v = $<HTMLSelectElement>(k).value;
      if (v) qs.set(k, v);
    }
    for (const k of SLIDERS) qs.set(k, $<HTMLInputElement>(k).value);
    const t0 = performance.now();
    try {
      const r = await api<RenderResponse>(`${API}/api/stones/${stone.slug}/render?${qs}`, { signal: abort.signal });
      // The material only swaps the photo: layers are the same for every material of a stone.
      const stoneImg = $<HTMLImageElement>('stoneImg');
      if (stoneImg.src !== r.material.imageUrl) stoneImg.src = r.material.imageUrl;
      await Promise.all([showLayer('text', r), showLayer('decor', r)]);
      const line = (l: Layer) => {
        const p = r.layers[l];
        return p ? `${l}: ${p.cache} ${p.renderMs} ms · ${p.width}×${p.height} at (${p.x}, ${p.y})` : `${l}: none`;
      };
      const overflow = !!r.layers.text?.overflow;
      const stats = $('stats');
      stats.textContent = `material: ${r.material.name} · color: ${r.settings.color}\n${line('text')}\n${line('decor')}\nround-trip incl. PNGs: ${(performance.now() - t0).toFixed(0)} ms${overflow ? '\n⚠ text goes outside the text area' : ''}`;
      stats.className = overflow ? 'stats warn' : 'stats';
      $('json').textContent = JSON.stringify({ ...r, settings: undefined }, null, 2);
    } catch (err) {
      if ((err as Error).name !== 'AbortError') $('stats').textContent = (err as Error).message;
    }
  }, 120);
}

$('text').addEventListener('input', update);
for (const k of SELECTS) $(k).addEventListener('input', update);
$<HTMLSelectElement>('stone').addEventListener('change', (e) => void loadStone((e.target as HTMLSelectElement).value));

const stones = await api<PublicStone[]>(`${API}/api/stones`);
$('stone').innerHTML = stones.map((s) => `<option value="${s.slug}">${esc(s.name)}</option>`).join('');
if (stones[0]) await loadStone(stones[0].slug);
else $('stats').textContent = 'No stones yet. Create one in /admin.';
